type ValidatorResult = string | boolean;

type Options = {
  delayMs?: number;
  cache?: boolean;
};

export function createDebouncedAsyncValidator<R extends ValidatorResult>(
  fn: (value: string) => Promise<R>,
  options: Options = {}
): (value: string) => Promise<R | true> {
  const { delayMs = 500, cache: enableCache = true } = options;
  const cache = new Map<string, R>();

  let timer: ReturnType<typeof setTimeout> | null = null;
  let pendingResolve: ((v: R | true) => void) | null = null;
  let pendingValue: string | null = null;

  return (value: string): Promise<R | true> => {
    if (enableCache && cache.has(value)) {
      return Promise.resolve(cache.get(value) as R);
    }

    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (pendingResolve && pendingValue !== value) {
      pendingResolve(true);
      pendingResolve = null;
      pendingValue = null;
    }

    return new Promise<R | true>((resolve) => {
      pendingResolve = resolve;
      pendingValue = value;
      timer = setTimeout(async () => {
        try {
          const result = await fn(value);
          if (enableCache) cache.set(value, result);
          resolve(result);
        } catch {
          resolve(true);
        } finally {
          if (pendingValue === value) {
            pendingResolve = null;
            pendingValue = null;
          }
          timer = null;
        }
      }, delayMs);
    });
  };
}
